import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import {
  Card, CardBody, CardHeader, Button, FormGroup, Label, Input,
  Spinner, Row, Col,
} from "reactstrap";
import PageTitleAlt2 from "../../Layout/AppMain/PageTitleAlt2";
import { STRATEGY_OPTIONS, strategyLabel } from "../../constants/strategies";
import { useAccount } from "../../context/AccountContext";

const PH_TYPES = new Set(["PYRAMID_HEDGING", "FUTURES_PYRAMID_HEDGING"]);

const STATUS_COLORS = {
  RUNNING:   { bg: "#fff3cd", color: "#856404" },
  COMPLETED: { bg: "#d4edda", color: "#155724" },
  FAILED:    { bg: "#fde2e2", color: "#721c24" },
  PENDING:   { bg: "#e3f2fd", color: "#0d47a1" },
};

function SessionRow({ s, onResume, onDelete, isInteractive }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const meta = STATUS_COLORS[s.status] || { bg: "#f8f9fa", color: "#495057" };
  const daysDone = s.progress || 0;
  const daysTotal = s.total_trading_days || 0;
  const pct = daysTotal ? Math.round((daysDone / daysTotal) * 100) : 0;

  return (
    <div style={srWrap}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#2c3e50" }}>
            {s.symbol} · {strategyLabel(s.strategy_type)}
          </div>
          <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
            {s.start_date} → {s.end_date}
            {s.expiry ? ` · Exp ${s.expiry}` : ""}
          </div>
          {daysTotal > 0 && (
            <div style={{ marginTop: 4 }}>
              <div style={{ height: 4, background: "#e9ecef", borderRadius: 2, width: 160 }}>
                <div style={{ height: 4, background: "#3b82f6", borderRadius: 2, width: `${pct}%`, transition: "width 0.3s" }} />
              </div>
              <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>
                Day {daysDone} of {daysTotal} · {pct}%
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <span style={{ ...srBadge, background: meta.bg, color: meta.color }}>{s.status}</span>
        {isInteractive && s.status === "RUNNING" && (
          <Button size="sm" color="primary" onClick={() => onResume(s.id)}>Resume</Button>
        )}
        {!isInteractive && (s.status === "COMPLETED" || s.status === "RUNNING") && (
          <Button size="sm" color="secondary" onClick={() => onResume(s.id)}>View Replay</Button>
        )}
        {confirmDelete ? (
          <>
            <span style={{ fontSize: 11, color: "#721c24" }}>Delete?</span>
            <Button size="sm" color="danger" onClick={() => onDelete(s.id)}>Yes</Button>
            <Button size="sm" color="link" style={{ padding: "2px 6px", fontSize: 11 }} onClick={() => setConfirmDelete(false)}>Cancel</Button>
          </>
        ) : (
          <button onClick={() => setConfirmDelete(true)} style={delBtn} title="Delete">✕</button>
        )}
      </div>
    </div>
  );
}

export default function BacktestConfig() {
  const history = useHistory();
  const { selectedAccount } = useAccount();
  const isAllAccounts = !selectedAccount || selectedAccount === "All Accounts";

  // Mode: "interactive" | "autorun"
  const [mode, setMode]             = useState("interactive");

  // Shared config fields
  const [symbols, setSymbols]       = useState([]);
  const [symbol, setSymbol]         = useState("");
  const [strategyType, setStrategyType] = useState("PYRAMID_HEDGING");
  const [startDate, setStartDate]   = useState("");
  const [endDate, setEndDate]       = useState("");
  const [initialQty, setInitialQty] = useState(0);
  const [dateRange, setDateRange]   = useState(null);
  const [expiryCadence, setExpiryCadence] = useState("monthly");
  const [checkInterval, setCheckInterval] = useState("5min");
  const [expiry, setExpiry]         = useState("");
  const [expiryOptions, setExpiryOptions] = useState([]);
  const [loadingExpiries, setLoadingExpiries] = useState(false);
  const [loadingSymbols, setLoadingSymbols] = useState(true);
  const [loading, setLoading]       = useState(false);
  const [err, setErr]               = useState(null);

  // Live strategy hint
  const [liveInfo, setLiveInfo]     = useState(null);

  // Sessions for each mode
  const [interactiveSessions, setInteractiveSessions] = useState([]);
  const [batchJobs, setBatchJobs]   = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const isPHType = PH_TYPES.has(strategyType);

  // Load previously-used symbols on mount, for autocomplete suggestions only —
  // any symbol can be typed, a cold one is backfilled on demand from Massive.
  useEffect(() => {
    axios.get("/backtest/interactive/symbols")
      .then(r => setSymbols(r.data))
      .catch(() => {})
      .finally(() => setLoadingSymbols(false));
  }, []);

  // Fixed ~2yr window (Massive's history ceiling) — loaded once, not per-symbol.
  // Only used to bound the date pickers; doesn't pre-fill start/end (a 2yr
  // default range would make every fresh backtest span the full window).
  useEffect(() => {
    axios.get("/backtest/interactive/date-range")
      .then(r => setDateRange(r.data))
      .catch(() => setDateRange(null));
  }, []);

  // Load sessions + jobs for the currently selected account, and reload when it changes
  useEffect(() => {
    if (isAllAccounts) {
      setInteractiveSessions([]);
      setBatchJobs([]);
      return;
    }

    axios.get("/backtest/interactive/sessions", { params: { account: selectedAccount } })
      .then(r => setInteractiveSessions(r.data))
      .catch(() => {});

    axios.get("/backtest/list", { params: { account: selectedAccount } })
      .then(r => setBatchJobs((r.data || []).filter(j => !j.is_interactive)))
      .catch(() => {});
  }, [selectedAccount, isAllAccounts]);

  // Load the current + next 2 real listed expiries of the chosen cadence
  // whenever symbol, end date, or cadence changes, debounced so free-text
  // symbol typing doesn't fire a Massive call per keystroke. This cadence
  // also locks in what every roll during the backtest will target — see
  // services/demo_symbol_provisioning.py:get_next_expiry_for_backtest.
  useEffect(() => {
    if (!symbol || !endDate) {
      setExpiryOptions([]);
      setExpiry("");
      return;
    }
    setLoadingExpiries(true);
    const t = setTimeout(() => {
      axios.get("/backtest/interactive/expiries", { params: { symbol, end_date: endDate, cadence: expiryCadence } })
        .then(r => {
          const opts = r.data.expiries || [];
          setExpiryOptions(opts);
          setExpiry((opts[0] || {}).date || "");
        })
        .catch(() => { setExpiryOptions([]); setExpiry(""); })
        .finally(() => setLoadingExpiries(false));
    }, 500);
    return () => clearTimeout(t);
  }, [symbol, endDate, expiryCadence]);

  // Load live strategy info when symbol + type + account changes
  useEffect(() => {
    if (!symbol || !strategyType || isAllAccounts) return;
    setLiveInfo(null);
    axios.get("/backtest/interactive/live-strategy", {
      params: { symbol, strategy_type: strategyType, account: selectedAccount },
    })
      .then(r => {
        setLiveInfo(r.data);
        if (r.data.found && r.data.current_stock_qty > 0 && isPHType) {
          setInitialQty(r.data.current_stock_qty);
        }
      })
      .catch(() => {});
  }, [symbol, strategyType, selectedAccount, isAllAccounts]);

  const handleDeleteInteractive = useCallback(async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`/backtest/interactive/${id}`);
      setInteractiveSessions(prev => prev.filter(s => s.id !== id));
    } catch (ex) {
      setErr(ex.response?.data?.error || "Failed to delete session.");
    } finally {
      setDeletingId(null);
    }
  }, []);

  const handleDeleteBatch = useCallback(async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`/backtest/${id}`);
      setBatchJobs(prev => prev.filter(s => s.id !== id));
    } catch (ex) {
      setErr(ex.response?.data?.error || "Failed to delete backtest.");
    } finally {
      setDeletingId(null);
    }
  }, []);

  const validate = useCallback(() => {
    if (isAllAccounts) return "Select a specific account before running a backtest.";
    if (!symbol || !startDate || !endDate) return "Please fill in all required fields.";
    if (endDate <= startDate) return "End date must be after start date.";
    return null;
  }, [symbol, startDate, endDate, isAllAccounts]);

  const handleInteractiveStart = async () => {
    const e = validate();
    if (e) { setErr(e); return; }
    setErr(null); setLoading(true);
    try {
      const r = await axios.post("/backtest/interactive/start", {
        account: selectedAccount,
        symbol, strategy_type: strategyType,
        start_date: startDate, end_date: endDate,
        expiry, expiry_cadence: expiryCadence, initial_qty: initialQty,
      });
      history.push(`/backtest/interactive/${r.data.session_id}`);
    } catch (ex) {
      setErr(ex.response?.data?.error || "Failed to start session.");
      setLoading(false);
    }
  };

  const handleAutoRun = async () => {
    const e = validate();
    if (e) { setErr(e); return; }
    setErr(null); setLoading(true);
    try {
      const r = await axios.post("/backtest/run", {
        account: selectedAccount,
        symbol, strategy_type: strategyType,
        start_date: startDate, end_date: endDate,
        expiry, expiry_cadence: expiryCadence, initial_qty: initialQty,
        check_interval: checkInterval,
      });
      history.push(`/backtest/replay/${r.data.job_id}`);
    } catch (ex) {
      setErr(ex.response?.data?.error || "Failed to start backtest.");
      setLoading(false);
    }
  };

  return (
    <div>
      <PageTitleAlt2
        heading="Backtest"
        subheading="Test a strategy against historical data — step through manually or auto-run the full period."
        icon="pe-7s-graph2 icon-gradient bg-ripe-malinaca"
      />

      <Row>
        {/* ── Config form ── */}
        <Col lg={5} md={6}>
          <Card className="mb-3">
            <CardHeader style={cardHdr}>
              <div style={{ display: "flex", gap: 0, borderRadius: 6, overflow: "hidden", border: "1px solid #dee2e6" }}>
                {[
                  { key: "interactive", label: "Interactive", desc: "Step manually" },
                  { key: "autorun",     label: "Auto-Run",    desc: "Full period replay" },
                ].map(m => (
                  <button
                    key={m.key}
                    onClick={() => setMode(m.key)}
                    style={{
                      flex: 1, border: "none", cursor: "pointer", padding: "6px 12px",
                      background: mode === m.key ? "#3b82f6" : "#fff",
                      color:      mode === m.key ? "#fff"    : "#495057",
                      fontSize: 12, fontWeight: 600,
                      transition: "background 0.15s",
                    }}
                  >
                    {m.label}
                    <div style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>{m.desc}</div>
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardBody>
              {err && (
                <div style={{ background: "#fde2e2", color: "#721c24", padding: "8px 12px", borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
                  {err}
                </div>
              )}

              {isAllAccounts ? (
                <div style={{ background: "#fff3cd", color: "#856404", padding: "8px 12px", borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
                  Select a specific account from the account switcher — backtests run against that account's strategy settings.
                </div>
              ) : (
                <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>
                  Using strategy settings from account: <strong>{selectedAccount}</strong>
                </div>
              )}

              <FormGroup>
                <Label style={lbl}>Symbol</Label>
                <Input
                  type="text"
                  list="backtest-symbol-suggestions"
                  placeholder="e.g. AAPL"
                  value={symbol}
                  onChange={e => setSymbol(e.target.value.toUpperCase())}
                />
                <datalist id="backtest-symbol-suggestions">
                  {symbols.map(s => <option key={s} value={s} />)}
                </datalist>
                <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                  Any symbol — 2 years of historical data available{loadingSymbols ? "" : symbols.length ? " · previously used: " + symbols.slice(0, 8).join(", ") + (symbols.length > 8 ? "…" : "") : ""}
                </div>
              </FormGroup>

              <FormGroup>
                <Label style={lbl}>Strategy Type</Label>
                <Input type="select" value={strategyType} onChange={e => setStrategyType(e.target.value)}>
                  {STRATEGY_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Input>
              </FormGroup>

              {/* Live strategy hint */}
              {liveInfo && liveInfo.found && (
                <div style={liveHint}>
                  <span style={{ color: "#28a745", marginRight: 6 }}>✓</span>
                  You have an active <strong>{symbol} {strategyLabel(strategyType)}</strong> strategy
                  {liveInfo.current_stock_qty > 0 && isPHType
                    ? <> · <strong>{liveInfo.current_stock_qty} shares</strong> pre-filled below</>
                    : null
                  }
                </div>
              )}

              <Row>
                <Col>
                  <FormGroup>
                    <Label style={lbl}>Start Date</Label>
                    <Input type="date" value={startDate}
                      min={dateRange?.min_date} max={dateRange?.max_date}
                      onChange={e => setStartDate(e.target.value)} />
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup>
                    <Label style={lbl}>End Date</Label>
                    <Input type="date" value={endDate}
                      min={startDate || dateRange?.min_date} max={dateRange?.max_date}
                      onChange={e => setEndDate(e.target.value)} />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label style={lbl}>Expiry Cadence</Label>
                <div style={{ display: "flex", gap: 0, borderRadius: 6, overflow: "hidden", border: "1px solid #dee2e6", width: 200 }}>
                  {[
                    { key: "monthly", label: "Monthly" },
                    { key: "weekly",  label: "Weekly" },
                  ].map(c => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setExpiryCadence(c.key)}
                      style={{
                        flex: 1, border: "none", cursor: "pointer", padding: "5px 10px",
                        background: expiryCadence === c.key ? "#3b82f6" : "#fff",
                        color:      expiryCadence === c.key ? "#fff"    : "#495057",
                        fontSize: 12, fontWeight: 600,
                        transition: "background 0.15s",
                      }}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                  Every roll during the backtest stays on this cadence.
                </div>
              </FormGroup>

              <FormGroup>
                <Label style={lbl}>Check Interval</Label>
                <div style={{ display: "flex", gap: 0, borderRadius: 6, overflow: "hidden", border: "1px solid #dee2e6", width: 200 }}>
                  {[
                    { key: "5min",   label: "5 Minutes" },
                    { key: "hourly", label: "Hourly" },
                  ].map(c => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setCheckInterval(c.key)}
                      style={{
                        flex: 1, border: "none", cursor: "pointer", padding: "5px 10px",
                        background: checkInterval === c.key ? "#3b82f6" : "#fff",
                        color:      checkInterval === c.key ? "#fff"    : "#495057",
                        fontSize: 12, fontWeight: 600,
                        transition: "background 0.15s",
                      }}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                  {checkInterval === "5min"
                    ? "Matches live trading's real 5-minute cycle exactly — most accurate, slower on volatile ranges."
                    : "Checks once per hour intraday — much faster on longer or volatile ranges, at slightly coarser timing."}
                  {" "}Applies to Run Automatically only, not the interactive step-through session.
                </div>
              </FormGroup>

              <FormGroup>
                <Label style={lbl}>
                  Expiry
                  <span style={{ fontWeight: 400, color: "#888", marginLeft: 6 }}>
                    (current + next 2 real {expiryCadence} expiries)
                  </span>
                </Label>
                {loadingExpiries ? (
                  <div style={{ color: "#888", fontSize: 13 }}><Spinner size="sm" /> Loading…</div>
                ) : expiryOptions.length > 0 ? (
                  <Input type="select" value={expiry} onChange={e => setExpiry(e.target.value)}>
                    {expiryOptions.map(o => (
                      <option key={o.date} value={o.date}>
                        {o.date}{o.monthly ? " (Monthly)" : " (Weekly)"}
                      </option>
                    ))}
                  </Input>
                ) : (
                  <div style={{ fontSize: 11, color: "#888" }}>
                    {symbol && endDate
                      ? `No listed ${expiryCadence} expiries found for this window — will auto-select the nearest real expiry at start.`
                      : "Enter a symbol and end date to see real listed expiries."}
                  </div>
                )}
              </FormGroup>

              {isPHType && (
                <FormGroup>
                  <Label style={lbl}>
                    Initial Stock Qty
                    <span style={{ fontWeight: 400, color: "#888", marginLeft: 6 }}>
                      (0 = start fresh)
                    </span>
                  </Label>
                  <Input type="number" min={0} step={1} value={initialQty}
                    onChange={e => setInitialQty(Math.max(0, parseInt(e.target.value) || 0))}
                    style={{ width: 120 }} />
                </FormGroup>
              )}

              {mode === "interactive" ? (
                <>
                  <Button color="primary" onClick={handleInteractiveStart}
                    disabled={loading || !symbol || isAllAccounts}
                    style={{ width: "100%", marginTop: 4 }}>
                    {loading ? <><Spinner size="sm" /> Starting…</> : "▶ Start Interactive Backtest"}
                  </Button>
                  <div style={howItWorks}>
                    <strong>Interactive:</strong> Step forward one day or hour at a time. Suggestions appear when the rule engine fires — approve them to execute, or skip to see the counterfactual.
                  </div>
                </>
              ) : (
                <>
                  <Button color="success" onClick={handleAutoRun}
                    disabled={loading || !symbol || isAllAccounts}
                    style={{ width: "100%", marginTop: 4 }}>
                    {loading ? <><Spinner size="sm" /> Queuing…</> : "▶▶ Run Full Backtest"}
                  </Button>
                  <div style={howItWorks}>
                    <strong>Auto-Run:</strong> The full period simulates in the background with auto-approved actions. When complete, a replay player lets you scrub, play, and review every day.
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* ── Prior sessions ── */}
        <Col lg={7} md={6}>
          <Card>
            <CardHeader style={cardHdr}>
              {mode === "interactive" ? "Interactive Sessions" : "Auto-Run History"}
            </CardHeader>
            <CardBody style={{ padding: 0 }}>
              {mode === "interactive" ? (
                interactiveSessions.length === 0 ? (
                  <div style={emptyState}>No interactive sessions yet.</div>
                ) : (
                  interactiveSessions.map(s => (
                    <SessionRow key={s.id} s={s} isInteractive
                      onResume={id => history.push(`/backtest/interactive/${id}`)}
                      onDelete={handleDeleteInteractive} />
                  ))
                )
              ) : (
                batchJobs.length === 0 ? (
                  <div style={emptyState}>No auto-run backtests yet.</div>
                ) : (
                  batchJobs.map(s => (
                    <SessionRow key={s.id} s={s} isInteractive={false}
                      onResume={id => history.push(`/backtest/replay/${id}`)}
                      onDelete={handleDeleteBatch} />
                  ))
                )
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

const cardHdr  = { fontWeight: 600, fontSize: 14, padding: "10px 16px", background: "#f8f9fa", borderBottom: "1px solid #e9ecef" };
const lbl      = { fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 };
const srWrap   = { display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: "1px solid #f0f0f0" };
const srBadge  = { fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.4px" };
const emptyState = { color: "#bbb", fontSize: 13, textAlign: "center", padding: "24px 0" };
const howItWorks = { marginTop: 10, padding: "8px 10px", background: "#f8f9fa", borderRadius: 6, fontSize: 11, color: "#555", lineHeight: 1.5 };
const liveHint = { fontSize: 12, color: "#1b5e20", background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 6, padding: "7px 10px", marginBottom: 12 };
const delBtn   = { background: "none", border: "none", cursor: "pointer", color: "#aaa", fontSize: 14, lineHeight: 1, padding: "2px 4px", borderRadius: 3, transition: "color 0.15s" };
